import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowClockwise,
  Bank,
  CheckCircle,
  DownloadSimple,
  FileArrowUp,
  Prohibit,
  UploadSimple,
  XCircle,
} from '@phosphor-icons/react'
import { isApiError } from '@/api'
import { useAuth } from '@/auth/context'
import { useFormat } from '@/i18n'
import { istDateKey } from '@/lib/ist'
import { Banner, Button, Field, Input, Modal, TextArea } from '@/ui'
import { errorDetail, errorMessage } from '../api/core'
import { useScope } from '../api/appConfig'
import { useExceptions } from '../api/exceptions'
import {
  BLOCKER_LABEL,
  PAY_AGAIN_ROUTES,
  payHoldLabel,
  PAYOUT_STATUS_LABEL,
  failureClockHours,
  maskAccount,
  ownWorkReason,
  useApprovePayout,
  useCancelPayout,
  useConfirmManual,
  useCreateBulkFile,
  useImportBulkResults,
  useManualTransfer,
  useMarkBulkUploaded,
  useMarkFailed,
  usePayAgain,
  usePayout,
  usePayouts,
  useBankAccounts,
  type Blocker,
  type PayoutFilters,
  type PayAgainRoute,
  type PayoutDetail,
  type PayoutRow,
} from '../api/payouts'
import { arr, obj, str } from '../api/mappers'
import { uploadEvidence } from '../api/reference'
import { useLot } from '../api/gate'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { DetailDrawer } from '../components/DetailDrawer'
import { QueryState } from '../components/Layout'
import { PhotoViewer } from '../components/PhotoViewer'
import {
  BoardColumns,
  BoardTable,
  BoardPage,
  Counter,
  Counters,
  FactRow,
  Facts as BoardFacts,
  LinkButton,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  Tag,
  TwoLine,
  type BoardColumn,
} from '../components/board'
import {
  approveOwnWork,
  blockerText,
  boardDate,
  failureIsAboutAccount,
  hoursSinceIso,
  isFailed,
  lotTag,
  payoutPill,
  reasonOrBlocker,
} from '../model/payoutBoard'
import { DayButton } from './payouts/shared'
import { useOpenFailures } from './payouts/useOpenFailures'
import { Facts, Money, RefLink, Status, Stream, UserName } from '../components/bits'
import { Timeline } from '../components/Timeline'
import { downloadText } from '../lib/csv'
import { useUrlState } from '../lib/useUrlState'
import { humanize } from '../labels'

const LIMIT = 50

/**
 * Payouts (alignment map V1-F4-09 PayoutsConsole; F4.9-i/k): counters, the table with each lot's
 * status and blocker, and the selected lot's panel (why it waits, the page photo, the amount, the
 * account, blockers, the two-person rule, Approve / Cancel with a reason). Bulk file, manual
 * transfer and the full try history stay reachable from the header and "All details".
 */
export function PayoutsPage() {
  const [f, setF] = useUrlState({
    day: '',
    status: '',
    kind: '',
    sessionId: '',
    farmerId: '',
    page: 1,
    open: '',
    details: '',
  })
  const { v1 } = useScope()
  const base = {
    day: f.day || undefined,
    kind: (f.kind || undefined) as PayoutFilters['kind'],
    sessionId: f.sessionId || undefined,
    farmerId: f.farmerId || undefined,
  }
  const q = usePayouts({ ...base, page: f.page, limit: LIMIT, status: f.status || undefined })
  const waiting = usePayouts({ ...base, status: 'PENDING_APPROVAL', page: 1, limit: 1 })
  const ready = usePayouts({ ...base, status: 'READY', page: 1, limit: 1 })
  const paid = usePayouts({ status: 'PROCESSED', page: 1, limit: 100 })
  const failures = useOpenFailures()
  const today = istDateKey()
  const paidToday = (paid.data?.rows ?? []).filter(
    (r) => r.processedAt && istDateKey(r.processedAt) === today,
  ).length

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulk, setBulk] = useState<'results' | null>(null)
  const createBulk = useCreateBulkFile()
  const rows = q.data?.rows ?? []
  const openId = f.open || rows[0]?.id || null
  const selectable = (r: PayoutRow) => r.status === 'READY'
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const allReady = rows.filter(selectable)
  const allOn = allReady.length > 0 && allReady.every((r) => selected.has(r.id))
  const toggleStatus = (status: string) => setF({ status: f.status === status ? '' : status })

  const pickColumn: BoardColumn<PayoutRow> = {
    id: 'pick',
    header: (
      <input
        type="checkbox"
        aria-label="Select every ready payment on this page for the bulk file"
        className="h-4 w-4 accent-amber"
        checked={allOn}
        disabled={allReady.length === 0}
        onChange={() => setSelected(allOn ? new Set() : new Set(allReady.map((r) => r.id)))}
      />
    ),
    width: '32px',
    cell: (r) =>
      selectable(r) ? (
        <input
          type="checkbox"
          aria-label={`Select ${r.unitCode} for the bulk file`}
          className="h-4 w-4 accent-amber"
          checked={selected.has(r.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggle(r.id)}
        />
      ) : null,
  }
  const columns: BoardColumn<PayoutRow>[] = [
    // V1 has no bulk file: no ticking rows for it.
    ...(v1 ? [] : [pickColumn]),
    {
      id: 'lot',
      header: 'Lot',
      cell: (r) => {
        const tag = lotTag(r)
        return (
          <TwoLine top={r.unitCode}>
            <span className="flex flex-wrap gap-1">
              <Tag tone={tag.tone}>{tag.text}</Tag>
              {r.kind === 'TOP_UP' && <Tag tone="help">Top-up</Tag>}
              {r.attemptNo > 1 && <Tag tone="plain">Try {r.attemptNo}</Tag>}
            </span>
          </TwoLine>
        )
      },
    },
    {
      id: 'farmer',
      header: 'Farmer',
      cell: (r) => <TwoLine top={r.farmerName ?? '—'} sub={r.villageName ?? undefined} />,
      hideBelow: 'sm',
    },
    {
      id: 'amount',
      header: 'Amount',
      align: 'right',
      cell: (r) => <Money paise={r.amountPaise} className="font-sans" />,
    },
    {
      id: 'status',
      header: 'Status · bank ref.',
      cell: (r) => {
        const pill = payoutPill(r)
        return (
          <span className="flex flex-col items-start gap-1">
            <Pill tone={pill.tone}>{pill.text}</Pill>
            {r.utr && <span className="text-[14px] text-muted">UTR {r.utr}</span>}
          </span>
        )
      },
    },
    { id: 'reason', header: 'Reason or blocker', cell: (r) => reasonOrBlocker(r), hideBelow: 'md' },
  ]

  const exportBulk = async () => {
    try {
      const r = await createBulk.mutateAsync([...selected])
      if (r.csv) downloadText(`${r.bulkFileRef}.csv`, r.csv)
      setSelected(new Set())
    } catch {
      /* toasted */
    }
  }

  return (
    <BoardPage
      title="Payouts"
      actions={
        <>
          <DayButton day={f.day} onChange={(day) => setF({ day })} />
          <select
            aria-label="Kind"
            value={f.kind}
            onChange={(e) => setF({ kind: e.target.value })}
            className="h-10 rounded-chip border-[1.5px] border-[#8a827a] bg-surface px-2 text-[15px]"
          >
            <option value="">Lots and top-ups</option>
            <option value="LOT">Lot payments</option>
            <option value="TOP_UP">Top-ups</option>
          </select>
          <select
            aria-label="Status"
            value={f.status}
            onChange={(e) => setF({ status: e.target.value })}
            className="h-10 rounded-chip border-[1.5px] border-[#8a827a] bg-surface px-2 text-[15px]"
          >
            <option value="">Every status</option>
            {Object.entries(PAYOUT_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {(f.sessionId || f.farmerId) && (
            <button
              type="button"
              className="h-10 rounded-chip border-[1.5px] border-[#8a827a] bg-surface px-3 text-[15px]"
              onClick={() => setF({ sessionId: '', farmerId: '' })}
            >
              {f.farmerId ? 'One farmer' : 'One day'} ✕
            </button>
          )}
          {!v1 && (
            <>
              <ActionButton
                perm="payouts:bulk-file"
                variant="secondary"
                leadingIcon={<FileArrowUp />}
                onClick={() => setBulk('results')}
              >
                Bulk file results
              </ActionButton>
              <ActionButton
                perm="payouts:bulk-file"
                variant="secondary"
                leadingIcon={<DownloadSimple />}
                disabled={selected.size === 0}
                title={selected.size === 0 ? 'Tick ready payments in the table first' : undefined}
                loading={createBulk.isPending}
                onClick={() => void exportBulk()}
              >
                Export bulk file{selected.size ? ` (${selected.size})` : ''}
              </ActionButton>
            </>
          )}
        </>
      }
    >
      <FundingBanner />
      {createBulk.data && createBulk.data.refused.length > 0 && (
        <Banner
          tone="warn"
          title={`${createBulk.data.refused.length} payments were left out of ${createBulk.data.bulkFileRef}`}
        >
          {createBulk.data.refused.map((x) => `${x.payoutId.slice(0, 8)}: ${x.reason}`).join(' · ')}
        </Banner>
      )}
      <BoardColumns
        main={
          <>
            <Counters>
              <Counter
                label="Waiting for approval"
                value={waiting.data?.total ?? 0}
                loading={waiting.isLoading}
                active={f.status === 'PENDING_APPROVAL'}
                onClick={() => toggleStatus('PENDING_APPROVAL')}
              />
              <Counter
                label="Ready to send"
                value={ready.data?.total ?? 0}
                loading={ready.isLoading}
                active={f.status === 'READY'}
                onClick={() => toggleStatus('READY')}
              />
              <Counter
                label="Paid today"
                value={paidToday}
                loading={paid.isLoading}
                active={f.status === 'PROCESSED'}
                onClick={() => toggleStatus('PROCESSED')}
              />
              <Counter
                label="Failed or returned"
                value={failures.items.length}
                loading={failures.isLoading}
                to="/c/payouts/failed"
              />
            </Counters>
            <BoardTable<PayoutRow>
              caption="Payouts"
              columns={columns}
              rows={rows}
              getRowId={(r) => r.id}
              selectedId={openId}
              onRowClick={(r) => setF({ open: r.id, page: f.page })}
              loading={q.isLoading}
              error={q.isError ? errorMessage(q.error) : undefined}
              empty="No payouts. They are raised when a lot is closed at the gate."
              pager={{
                page: f.page,
                total: q.data?.total ?? 0,
                limit: LIMIT,
                onPage: (page) => setF({ page }),
              }}
            />
            <span className="text-[14px] leading-snug text-muted">
              Pay waits only when ownership or the amount is in doubt: tag clash, foreign tag,
              origin mismatch, revoked device, amount mismatch, rate card, no verified account; a
              paper-day lot waits for one approval. Quality flags keep a lot out of the brand but
              never hold his pay.
            </span>
          </>
        }
        side={
          openId ? (
            <PayoutPanel id={openId} onDetails={() => setF({ details: openId, page: f.page })} />
          ) : undefined
        }
      />
      <PayoutDrawer id={f.details || null} onClose={() => setF({ details: '', page: f.page })} />
      {!v1 && <BulkResultsDialog open={bulk === 'results'} onClose={() => setBulk(null)} />}
    </BoardPage>
  )
}

/** The selected lot (board's right panel). */
function PayoutPanel({ id, onDetails }: { id: string; onDetails: () => void }) {
  const { user } = useAuth()
  const q = usePayout(id)
  const p = q.data
  const lot = useLot(p?.unitId)
  const accounts = useBankAccounts(p?.farmerId ?? undefined)
  // A paper-day payment is approved only after the slip photo was opened (F4-B3.c).
  const [photoSeenFor, setPhotoSeenFor] = useState<string | null>(null)
  if (!p)
    return (
      <SidePanel title="Payout" label="Selected payout">
        <QueryState query={q} label="the payout">
          <span />
        </QueryState>
      </SidePanel>
    )
  const l = lot.data
  const paper = Boolean(l?.paperDay) || p.captureMode === 'PAPER'
  const typedById = l?.receiptSlip?.enteredById ?? null
  const pill = payoutPill(p)
  const waitedH = hoursSinceIso(p.requestedAt)
  const account = (accounts.data ?? []).find((a) => a.id === p.accountId)
  const own = approveOwnWork(
    p,
    l
      ? {
          committedById: l.committedById,
          sessionOperatorId: l.sessionOperatorId,
          paperDay: paper,
          typedById,
        }
      : null,
    user?.id,
  )
  const failed = isFailed(p.status)
  const realBlockers = p.status === 'PENDING_APPROVAL' ? p.blockers : []
  const photo = l?.receiptSlip?.photo ?? l?.slipPhoto ?? p.slipPhotoId ?? null
  const photoSeen = photoSeenFor === p.id
  const paperGate =
    paper && p.status === 'PENDING_APPROVAL' && !photoSeen
      ? 'Open the slip photo and check it against the typed lot first.'
      : null
  const sub = [
    p.villageName,
    `${p.stream === 'CONV' ? 'red-tie' : 'good'} lot`,
    paper && p.collectionDay
      ? `paper day ${boardDate(p.collectionDay)}`
      : p.collectionDay
        ? `closed ${boardDate(p.collectionDay)}`
        : null,
  ].filter(Boolean)
  return (
    <SidePanel
      label="Selected payout"
      title={`${p.unitCode} · ${p.farmerName ?? 'Farmer'}`}
      sub={
        <>
          {sub.join(' · ')}
          {paper && typedById && (
            <>
              {' '}
              · typed by <UserName id={typedById} />
            </>
          )}
        </>
      }
    >
      <Pill tone={pill.tone}>
        {p.status === 'PENDING_APPROVAL' && pill.tone === 'wait'
          ? `Waiting for approval${waitedH !== null ? ` · ${waitedH} h` : ''}`
          : pill.text}
        {p.utr ? ` · UTR ${p.utr}` : ''}
      </Pill>
      {p.status === 'PENDING_APPROVAL' && (
        <span className="text-[15px]">
          <b>Why:</b>{' '}
          {realBlockers.some((b) => !b.code.startsWith('PAPER_DAY'))
            ? realBlockers
                .filter((b) => !b.code.startsWith('PAPER_DAY'))
                .map(blockerText)
                .join(' · ')
            : paper
              ? 'paper day. Open the slip photo and check it against the typed lot; fix a wrong weight before approving.'
              : p.holdReason
                ? payHoldLabel(p.holdReason)
                : 'it waits for one approval.'}
        </span>
      )}
      {failed && (
        <span className="text-[15px]">
          <b>Why:</b> {p.status === 'REVERSED' ? 'returned by the bank' : 'the bank refused it'}:{' '}
          {p.failureReason ?? 'no reason given'}.
        </span>
      )}
      {paper && (
        <div className="flex flex-col gap-2 rounded-chip border-[1.5px] border-rule p-2">
          <div className="flex items-center gap-2.5">
            <PhotoViewer
              photo={photo}
              alt={`Slip of ${p.unitCode}: page photo`}
              size="sm"
              onOpen={() => setPhotoSeenFor(p.id)}
            />
            <span className="flex flex-1 flex-col text-[14px]">
              <span>
                {l?.receiptSlip
                  ? `Slip ${String(l.receiptSlip.slipNo).padStart(4, '0')}`
                  : l?.slipNo
                    ? `Slip ${String(l.slipNo).padStart(4, '0')}`
                    : 'Slip'}{' '}
                · page photo
              </span>
              {p.status === 'PENDING_APPROVAL' && (
                <span className={photoSeen ? 'text-[13px] text-ok' : 'text-[13px] text-warn'}>
                  {photoSeen ? 'Checked' : 'Tap to open before approving'}
                </span>
              )}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <LinkButton to={`/c/lots/${p.unitId}`}>Open the lot</LinkButton>
            {p.status === 'PENDING_APPROVAL' && (
              <LinkButton to={`/c/lots/${p.unitId}?correct=1`}>Correct a weight</LinkButton>
            )}
          </div>
        </div>
      )}
      <BoardFacts>
        <FactRow label="Lot value">
          <Money paise={p.lotValuePaise} className="font-sans" />
        </FactRow>
        {p.advanceRecoveredPaise > 0 && (
          <FactRow label="Advance recovered">
            <Money paise={-p.advanceRecoveredPaise} className="font-sans" />
          </FactRow>
        )}
        {p.overpaymentRecoveredPaise > 0 && (
          <FactRow label="Overpayment recovered">
            <Money paise={-p.overpaymentRecoveredPaise} className="font-sans" />
          </FactRow>
        )}
        <FactRow label="To send" strong>
          <Money paise={p.amountPaise} className="font-sans" />
        </FactRow>
      </BoardFacts>
      <div className="flex items-center gap-2">
        <Bank aria-hidden="true" size={18} className="shrink-0 text-muted" />
        <span className="text-[14px]">
          {p.accountId ? (
            <>
              {p.accountIfsc ?? '—'} · {maskAccount(p.accountLast4)} ·{' '}
              {p.accountStatus === 'VERIFIED'
                ? account?.verificationMethod === 'PENNY_DROP' ||
                  account?.verificationMethod === 'REVERSE_PENNY_DROP' ||
                  !account
                  ? 'verified through OnGrid'
                  : `verified (${humanize(account.verificationMethod ?? '')})`
                : (p.accountStatus ?? '').toLowerCase()}
            </>
          ) : (
            'No bank account yet'
          )}
        </span>
      </div>
      {p.status === 'PENDING_APPROVAL' &&
        (realBlockers.length === 0 ? (
          <Pill tone="ok">No blockers</Pill>
        ) : (
          <div className="flex flex-col gap-1.5">
            {realBlockers.map((b, i) => {
              const exId = typeof b.detail.exception_id === 'string' ? b.detail.exception_id : null
              return (
                <span key={i} className="flex flex-col items-start gap-0.5">
                  <Pill tone="bad" wrap>
                    {blockerText(b)}
                  </Pill>
                  {exId && (
                    <Link className="pl-1 text-[13px] underline" to={`/c/exceptions?open=${exId}`}>
                      Open the exception
                    </Link>
                  )}
                  {b.code === 'NO_VERIFIED_ACCOUNT' && p.farmerId && (
                    <Link
                      className="pl-1 text-[13px] underline"
                      to={`/c/farmers/${p.farmerId}/bank`}
                    >
                      Fix bank details
                    </Link>
                  )}
                </span>
              )
            })}
          </div>
        ))}
      {p.status === 'PENDING_APPROVAL' && own.ok && paperGate ? (
        <Note className="text-warn">{paperGate}</Note>
      ) : (
        p.status === 'PENDING_APPROVAL' &&
        own.text && <Note className={own.ok ? undefined : 'text-danger'}>{own.text}</Note>
      )}
      <div className="flex-1" />
      <PanelActions>
        <PayoutActions p={p} panel approveBlockedReason={own.ok ? paperGate : own.text} />
        <Button variant="ghost" size="sm" onClick={onDetails}>
          All details and tries
        </Button>
      </PanelActions>
    </SidePanel>
  )
}

function FailureClock({ p }: { p: Pick<PayoutRow, 'processedAt' | 'requestedAt'> }) {
  const h = failureClockHours(p)
  if (h === null) return null
  return (
    <span
      className={
        h >= 24
          ? 'font-mono text-[11.5px] font-bold text-danger'
          : 'font-mono text-[11.5px] text-warn'
      }
      title="Hours since the failure"
    >
      {h} h since failure
    </span>
  )
}

function PayoutDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const q = usePayout(id)
  const p = q.data
  return (
    <DetailDrawer
      open={Boolean(id)}
      onClose={onClose}
      width="lg"
      eyebrow={
        p ? `${p.kind === 'TOP_UP' ? 'Top-up' : 'Lot payment'} · try ${p.attemptNo}` : 'Payout'
      }
      title={p ? <span className="font-mono">{p.idempotencyKey ?? p.unitCode}</span> : 'Payout'}
      meta={p && <Status status={p.status} label={PAYOUT_STATUS_LABEL[p.status]} />}
      actions={p && <PayoutActions p={p} />}
    >
      <QueryState query={q} label="the payout">
        {p && <PayoutBody p={p} />}
      </QueryState>
    </DetailDrawer>
  )
}

function PayoutBody({ p }: { p: PayoutDetail }) {
  const fmt = useFormat()
  const { v1 } = useScope()
  const failed = p.status === 'FAILED' || p.status === 'REVERSED'
  return (
    <div className="flex flex-col gap-5">
      {failed && (
        <Banner tone="error" title="Payments · failure" action={<FailureClock p={p} />}>
          <div className="flex flex-col gap-1">
            <span>Reason from the bank: {p.failureReason ?? 'not given'}</span>
            <span className="font-semibold">
              {failureIsAboutAccount(p.failureReason)
                ? 'About the account — fix the bank details, then pay again.'
                : 'Worth trying again on the same account.'}
            </span>
            <span>Tries so far: {p.tries.length}</span>
          </div>
        </Banner>
      )}
      {p.blockers.length > 0 && <BlockerList blockers={p.blockers} />}
      <Facts
        columns={3}
        items={[
          { label: 'Lot', value: <RefLink to={`/c/lots/${p.unitId}`}>{p.unitCode}</RefLink> },
          { label: 'Stream', value: <Stream stream={p.stream} /> },
          { label: 'Collection day', value: p.collectionDay ?? '—', mono: true },
          {
            label: 'Farmer',
            value: p.farmerId ? (
              <Link className="underline" to={`/c/farmers/${p.farmerId}`}>
                {p.farmerName ?? 'Farmer'}
              </Link>
            ) : (
              '—'
            ),
          },
          { label: 'Village', value: p.villageName ?? '—' },
          { label: 'Mode', value: p.mode ?? '—', mono: true },
          { label: 'Lot value', value: <Money paise={p.lotValuePaise} /> },
          ...(v1
            ? []
            : [
                {
                  label: 'Advance recovered',
                  value: <Money paise={-p.advanceRecoveredPaise} signed />,
                },
              ]),
          {
            label: 'Overpayment recovered',
            value: <Money paise={-p.overpaymentRecoveredPaise} signed />,
          },
          {
            label: 'Amount to send',
            value: <Money paise={p.amountPaise} className="text-[17px] font-semibold" />,
          },
          {
            label: 'Account',
            value: (
              <span className="font-mono">
                {maskAccount(p.accountLast4)} · {p.accountIfsc ?? '—'}
              </span>
            ),
          },
          {
            label: 'Account status',
            value: p.accountStatus ? <Status status={p.accountStatus} /> : 'none',
          },
          { label: 'Waits because', value: p.holdReason ? payHoldLabel(p.holdReason) : '—' },
          { label: 'UTR', value: p.utr ?? '—', mono: true },
          ...(v1 ? [] : [{ label: 'Bulk file', value: p.bulkFileRef ?? '—', mono: true }]),
          { label: 'Raised by', value: <UserName id={p.requestedById} /> },
          { label: 'Approved by', value: <UserName id={p.approvedById} /> },
          { label: 'Transfer recorded by', value: <UserName id={p.manualUtrEnteredById} /> },
        ]}
      />
      <div>
        <div className="eyebrow mb-2">Tries</div>
        <Timeline
          items={p.tries.map((t) => ({
            id: t.id,
            at: t.requestedAt,
            title: (
              <span className="flex items-center gap-2">
                <span className="font-mono">#{t.attemptNo}</span>{' '}
                <Status status={t.status} label={PAYOUT_STATUS_LABEL[t.status]} />{' '}
                <Money paise={t.amountPaise} />
              </span>
            ),
            detail: (
              <span className="font-mono text-[12px]">
                {t.idempotencyKey} · {t.mode ?? '—'}
                {t.utr && ` · UTR ${t.utr}`}
                {t.failureReason && ` · ${t.failureReason}`}
                {t.processedAt && ` · done ${fmt.dateTime(t.processedAt)}`}
              </span>
            ),
            tone:
              t.status === 'PROCESSED'
                ? 'ok'
                : t.status === 'FAILED' || t.status === 'REVERSED'
                  ? 'danger'
                  : t.status === 'CANCELLED'
                    ? 'neutral'
                    : 'info',
          }))}
        />
      </div>
    </div>
  )
}

function BlockerList({ blockers }: { blockers: Blocker[] }) {
  return (
    <Banner tone="warn" title="What must be fixed before approval">
      <ul className="list-disc pl-4">
        {blockers.map((b, i) => {
          const exId = typeof b.detail.exception_id === 'string' ? b.detail.exception_id : null
          const toReweigh = arr(b.detail.bags_to_reweigh).length
          const out = arr(b.detail.out_of_tolerance).length
          return (
            <li key={i}>
              {BLOCKER_LABEL[b.code] ?? b.code}
              {b.code === 'PAPER_DAY' &&
                ` — ${toReweigh} bags to re-weigh, ${out} outside tolerance`}
              {exId && (
                <>
                  {' '}
                  —{' '}
                  <Link className="underline" to={`/c/exceptions?open=${exId}`}>
                    open the exception
                  </Link>
                </>
              )}
            </li>
          )
        })}
      </ul>
    </Banner>
  )
}

/**
 * Every action a payout's status allows. `panel` = the board's side panel (Approve, Cancel with a
 * reason, Fix bank details / Pay again as links to their own screens).
 */
function PayoutActions({
  p,
  panel,
  approveBlockedReason,
}: {
  p: PayoutDetail
  panel?: boolean
  approveBlockedReason?: string | null
}) {
  const { user } = useAuth()
  const [dialog, setDialog] = useState<
    'approve' | 'cancel' | 'again' | 'manual' | 'confirm' | 'failed' | null
  >(null)
  const [approveBlockers, setApproveBlockers] = useState<{
    message: string
    blockers: Blocker[]
  } | null>(null)
  const approve = useApprovePayout((e) => {
    if (isApiError(e) && e.kind === 'conflict') {
      const d = errorDetail(e)
      const fromBody = arr(d.blockers).map((b) => ({ code: str(b.code), detail: obj(b.detail) }))
      setApproveBlockers({
        message: errorMessage(e),
        blockers: fromBody.length ? fromBody : p.blockers,
      })
      return true
    }
  })
  const cancel = useCancelPayout()
  const confirm = useConfirmManual()
  const markFailed = useMarkFailed()
  const close = () => {
    setDialog(null)
    setApproveBlockers(null)
  }
  const s = p.status
  const failed = s === 'FAILED' || s === 'REVERSED'
  return (
    <>
      {s === 'PENDING_APPROVAL' && (
        <ActionButton
          perm="payouts:approve"
          leadingIcon={<CheckCircle />}
          blockedReason={ownWorkReason(p, user?.id, 'approve') ?? approveBlockedReason ?? null}
          onClick={() => setDialog('approve')}
        >
          Approve
        </ActionButton>
      )}
      {s === 'READY' && (
        <ActionButton
          perm="payouts:manual-entry"
          variant="secondary"
          leadingIcon={<Bank />}
          onClick={() => setDialog('manual')}
        >
          Record bank transfer
        </ActionButton>
      )}
      {s === 'SUBMITTING' && p.mode === 'MANUAL' && (
        <ActionButton
          perm="payouts:manual-confirm"
          leadingIcon={<CheckCircle />}
          blockedReason={ownWorkReason(p, user?.id, 'manual-confirm')}
          onClick={() => setDialog('confirm')}
        >
          Confirm bank transfer
        </ActionButton>
      )}
      {s === 'SUBMITTING' && p.mode === 'BULK_FILE' && (
        <ActionButton
          perm="payouts:mark-failed"
          variant="ghost"
          leadingIcon={<XCircle />}
          onClick={() => setDialog('failed')}
        >
          Mark failed
        </ActionButton>
      )}
      {failed && (
        <>
          {p.farmerId && (
            <Link
              to={`/c/farmers/${p.farmerId}/bank?payout=${p.id}`}
              className={
                panel
                  ? 'inline-flex h-10 items-center justify-center gap-2 rounded-chip border-[1.5px] border-[#8a827a] bg-surface px-4 text-[15px] font-bold hover:bg-paper-tint'
                  : 'inline-flex h-10 items-center gap-2 rounded-chip border border-ink/80 px-4 font-mono text-[12px] font-bold uppercase tracking-label hover:bg-ink hover:text-paper'
              }
            >
              <Bank aria-hidden="true" /> Fix bank details
            </Link>
          )}
          {panel ? (
            <Link
              to={`/c/payouts/${p.id}/pay-again`}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-chip border-[1.5px] border-amber bg-amber px-4 text-[15px] font-bold text-white hover:bg-amber-dark"
            >
              <ArrowClockwise aria-hidden="true" /> Pay again
            </Link>
          ) : (
            <ActionButton
              perm="payouts:pay-again"
              leadingIcon={<ArrowClockwise />}
              onClick={() => setDialog('again')}
            >
              Pay again
            </ActionButton>
          )}
        </>
      )}
      {(s === 'PENDING_APPROVAL' || s === 'READY') && (
        <ActionButton
          perm="payouts:cancel"
          variant={panel ? 'danger' : 'ghost'}
          leadingIcon={<Prohibit />}
          onClick={() => setDialog('cancel')}
        >
          {panel ? 'Cancel with a reason' : 'Cancel'}
        </ActionButton>
      )}

      <ConfirmDialog
        open={dialog === 'approve'}
        onClose={close}
        title={`Approve ${p.idempotencyKey ?? p.unitCode}`}
        description="The payment becomes ready to send to the bank."
        confirmLabel="Approve"
        loading={approve.isPending}
        onConfirm={() => approve.mutateAsync(p.id).then(close)}
      >
        <div className="rounded-card border border-rule bg-paper-sunk px-3 py-2 text-[14px]">
          <Money paise={p.amountPaise} className="text-[18px] font-semibold" /> to{' '}
          {p.farmerName ?? 'the farmer'} · {maskAccount(p.accountLast4)} {p.accountIfsc}
        </div>
        {approveBlockers ? (
          <>
            <Banner tone="error" title="Not approved">
              {approveBlockers.message}
            </Banner>
            <BlockerList blockers={approveBlockers.blockers} />
          </>
        ) : (
          p.blockers.length > 0 && <BlockerList blockers={p.blockers} />
        )}
      </ConfirmDialog>
      <ConfirmDialog
        open={dialog === 'cancel'}
        onClose={close}
        title="Cancel this payment"
        description="Nothing is sent. The lot's balance stays owed to the farmer."
        confirmLabel="Cancel payment"
        tone="danger"
        loading={cancel.isPending}
        reason={{ label: 'Reason', minLength: 4 }}
        onConfirm={(reason) => cancel.mutateAsync({ id: p.id, reason }).then(close)}
      />
      <ConfirmDialog
        open={dialog === 'confirm'}
        onClose={close}
        title="Confirm the bank transfer"
        description="Find this transfer in the bank portal and check every line below before confirming. This marks the payment paid and sends the PAID SMS."
        confirmLabel="Confirm — paid"
        loading={confirm.isPending}
        onConfirm={() => confirm.mutateAsync(p.id).then(close)}
      >
        <Facts
          columns={2}
          items={[
            { label: 'Amount', value: <Money paise={p.amountPaise} className="font-semibold" /> },
            { label: 'Bank reference (UTR)', value: p.utr ?? '—', mono: true },
            {
              label: 'To',
              value: `${p.farmerName ?? 'Farmer'} · ${maskAccount(p.accountLast4)} ${p.accountIfsc ?? ''}`,
            },
            {
              label: 'Recorded by',
              value: p.manualUtrEnteredByName ?? <UserName id={p.manualUtrEnteredById} />,
            },
          ]}
        />
      </ConfirmDialog>
      <ConfirmDialog
        open={dialog === 'failed'}
        onClose={close}
        title="Mark this bulk try failed"
        description="Only after the daily reconciliation found it was never at the provider (PAYOUT_RECON_GAP · BULK_NOT_AT_PROVIDER)."
        confirmLabel="Mark failed"
        tone="danger"
        loading={markFailed.isPending}
        reason={{ label: 'Note', minLength: 4 }}
        onConfirm={(note) => markFailed.mutateAsync({ id: p.id, note }).then(close)}
      />
      <TransferDialog mode="manual" open={dialog === 'manual'} onClose={close} p={p} />
      <TransferDialog mode="again" open={dialog === 'again'} onClose={close} p={p} />
    </>
  )
}

/** Record bank transfer (manual) or Pay again (four routes; UTR for a manual transfer). */
function TransferDialog({
  mode,
  open,
  onClose,
  p,
}: {
  mode: 'manual' | 'again'
  open: boolean
  onClose: () => void
  p: PayoutDetail
}) {
  const [route, setRoute] = useState<PayAgainRoute>(mode === 'manual' ? 'MANUAL' : 'SAME_ACCOUNT')
  const [utr, setUtr] = useState('')
  const [paidOn, setPaidOn] = useState(istDateKey())
  const [proof, setProof] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [touched, setTouched] = useState(false)
  const again = usePayAgain()
  const manual = useManualTransfer()
  const markUploaded = useMarkBulkUploaded()
  const { v1 } = useScope()
  const routes = PAY_AGAIN_ROUTES.filter((r) => !(v1 && r.value === 'BULK_FILE'))
  const isManual = mode === 'manual' || route === 'MANUAL'
  const utrOk = /^[A-Za-z0-9]{6,32}$/.test(utr.trim())
  const save = async () => {
    setTouched(true)
    if (isManual && (!utrOk || !paidOn)) return
    try {
      if (mode === 'manual')
        await manual.mutateAsync({
          id: p.id,
          utr: utr.trim(),
          paidOn,
          proofMediaId: proof ?? undefined,
        })
      else
        await again.mutateAsync({
          id: p.id,
          route,
          ...(isManual ? { utr: utr.trim(), paidOn, proofMediaId: proof ?? undefined } : {}),
        })
      onClose()
    } catch {
      /* toasted */
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'manual' ? 'Record bank transfer' : 'Pay again'}
      description={
        mode === 'manual'
          ? 'Only when RazorpayX cannot pay: pay from the bank portal and record it here. A second person confirms it against the bank before it counts as paid.'
          : `Try ${p.attemptNo + 1}. The amount is the failed try's — never typed. Each try has its own reference so it cannot be paid twice.`
      }
      footer={
        <>
          {mode === 'again' && route === 'BULK_FILE' && p.bulkFileRef && (
            <Button
              variant="ghost"
              leadingIcon={<UploadSimple />}
              loading={markUploaded.isPending}
              onClick={() => void markUploaded.mutateAsync(p.bulkFileRef!).catch(() => undefined)}
            >
              Mark uploaded
            </Button>
          )}
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={again.isPending || manual.isPending || busy} onClick={() => void save()}>
            {mode === 'manual' ? 'Record' : 'Pay'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-card border border-rule bg-paper-sunk px-3 py-2 text-[14px]">
          Amount <Money paise={p.amountPaise} className="font-semibold" /> · {p.farmerName ?? ''} ·{' '}
          {maskAccount(p.accountLast4)} {p.accountIfsc ?? ''}
        </div>
        {mode === 'again' && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-[14px] font-semibold">Route</legend>
            {routes.map((r) => (
              <label
                key={r.value}
                className="flex cursor-pointer items-start gap-2 rounded-card border border-rule bg-surface px-3 py-2 has-[:checked]:border-ink"
              >
                <input
                  type="radio"
                  name="route"
                  className="mt-1 accent-amber"
                  checked={route === r.value}
                  onChange={() => setRoute(r.value)}
                />
                <span>
                  <span className="block text-[14px] font-semibold">{r.label}</span>
                  <span className="block text-[13px] text-muted">{r.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        {isManual && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Bank reference (UTR)"
                required
                error={touched && !utrOk ? '6–32 letters or digits' : null}
              >
                <Input
                  value={utr}
                  onChange={(e) => setUtr(e.target.value)}
                  className="font-mono"
                  autoComplete="off"
                />
              </Field>
              <Field label="Paid on" required>
                <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
              </Field>
            </div>
            <Field label="Proof (screenshot)" hint="Optional">
              <input
                type="file"
                accept="image/*"
                className="text-[13px]"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  setBusy(true)
                  try {
                    setProof(await uploadEvidence(file))
                  } finally {
                    setBusy(false)
                  }
                }}
              />
            </Field>
            <p className="text-[13px] text-muted">
              Confirmed by: a second person, checked by login.
            </p>
          </>
        )}
      </div>
    </Modal>
  )
}

function BulkResultsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [ref, setRef] = useState('')
  const [csv, setCsv] = useState('')
  const uploaded = useMarkBulkUploaded()
  const results = useImportBulkResults()
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bulk file"
      description="After uploading the exported file in the bank portal, mark it uploaded. When the bank's result file is ready, paste or load it here — rows are matched on the reference id (the payout id)."
      footer={
        <>
          <Button
            variant="secondary"
            className="mr-auto"
            disabled={!ref.trim()}
            loading={uploaded.isPending}
            onClick={() => void uploaded.mutateAsync(ref.trim()).catch(() => undefined)}
          >
            Mark uploaded
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            disabled={!ref.trim() || !csv.trim()}
            loading={results.isPending}
            onClick={() =>
              void results.mutateAsync({ ref: ref.trim(), csv }).catch(() => undefined)
            }
          >
            Import results
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Bulk file reference" required>
          <Input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            className="font-mono"
            placeholder="e.g. BULK-20261018-01"
          />
        </Field>
        <Field label="Result file (CSV)">
          <input
            type="file"
            accept=".csv,text/csv"
            className="mb-2 text-[13px]"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (file) setCsv(await file.text())
            }}
          />
          <TextArea
            rows={6}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            className="font-mono text-[12px]"
            placeholder="…or paste the CSV"
          />
        </Field>
        {results.data && (
          <Banner tone="success" title={`${results.data.rows} rows applied`}>
            {Object.entries(
              results.data.results.reduce<Record<string, number>>(
                (m, r) => ((m[r.result] = (m[r.result] ?? 0) + 1), m),
                {},
              ),
            )
              .map(([k, n]) => `${n} ${k.toLowerCase()}`)
              .join(' · ')}
          </Banner>
        )}
      </div>
    </Modal>
  )
}

/**
 * RazorpayX funding and reconciliation (F4.9 V1): the central team keeps the account funded each
 * morning, and a payout Razorpay has that we do not (or the reverse) must be looked at today.
 */
function FundingBanner() {
  const low = useExceptions({ type: 'LOW_BALANCE', status: 'OPEN', page: 1, limit: 1 })
  const gap = useExceptions({ type: 'PAYOUT_RECON_GAP', status: 'OPEN', page: 1, limit: 1 })
  const lowN = low.data?.total ?? 0
  const gapN = gap.data?.total ?? 0
  if (!lowN && !gapN) return null
  return (
    <div className="flex flex-col gap-2">
      {lowN > 0 && (
        <Banner tone="warn" title="RazorpayX balance is low">
          {lowN} payment{lowN === 1 ? ' is' : 's are'} queued at RazorpayX until money is added to
          the account. Add funds, then they go by themselves.{' '}
          <Link className="underline" to="/c/exceptions?type=LOW_BALANCE&status=OPEN">
            See them
          </Link>
        </Banner>
      )}
      {gapN > 0 && (
        <Banner tone="error" title="Razorpay and our records do not match">
          The daily check found {gapN} payment{gapN === 1 ? '' : 's'} on one side only. Look at{' '}
          {gapN === 1 ? 'it' : 'them'} today.{' '}
          <Link className="underline" to="/c/exceptions?type=PAYOUT_RECON_GAP&status=OPEN">
            Open the gaps
          </Link>
        </Banner>
      )}
    </div>
  )
}
